import { useStyletron } from '@thanx/uikit/theme'
import { useGetRecoveryStatusQuery } from 'api/recoveryAi'
import SectionHeader from 'components/Navbar/Bar/SectionHeader'
import palette from 'constants/palette'
import useCurrentMerchant, {
  useMerchantFeedbackEnabled,
} from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import {
  formatPermissionReason,
  formatPermissionReasonOr,
  usePermissionLabel,
  usePermissionReason,
  useRbacNavDisabled,
  useRbacNavDisabledWithReason,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React from 'react'
import { useAbility } from 'utilities/ability'
import NavLink from '../Bar/NavLink'

const t = buildTranslate('nav.operations_center_menu')
const navT = buildTranslate('nav')

type PropsT = {
  isLeftNav?: boolean
  onClick?: () => void
  // Opens the NPS→RecoveryAI transition modal. Owned by the caller
  // (OperationsCenter), which has the RecoveryAI config needed to pick the
  // modal variant.
  onNpsClick?: () => void
}

const OperationsCenterContent = ({
  isLeftNav = false,
  onClick = () => {},
  onNpsClick,
}: PropsT) => {
  const [css] = useStyletron()
  const { orderingEnabled, supportsMenuErrors, supportsOrderingProvider } =
    useOrderingAccess()

  const orderingLabel = usePermissionLabel('OrderingManagement')
  const orderingReason = formatPermissionReason(
    orderingLabel,
    !orderingEnabled,
    t('ordering_suffix')
  )

  const userSupport = useRbacNavDisabledWithReason('UserSupport')

  // Phase 2 Consolidated Locations page. Gated on the same `operations-center`
  // flag as the rest of OC so the link only appears once the flag flips on
  // per merchant; legacy /locations under the profile menu remains the
  // source of truth until then.
  const locations = useRbacNavDisabledWithReason('Locations')

  // DATA-4953 Delivery & Location Settings consolidation. Uses
  // OrderingManagement (the editable sections' RBAC resource) — users
  // without it can't edit anyway and the read-only Ordering
  // Configuration reference sits behind the same gate.
  const deliveryLocationSettings =
    useRbacNavDisabledWithReason('OrderingManagement')

  // Alert Banner Settings is accessible to operators (OrderingManagement) and
  // marketing/CSM (CMS). Match the nav's Brand & Content OR pattern so either
  // permission unlocks the link.
  const orderingMgmtDisabled = useRbacNavDisabled('OrderingManagement')
  const cmsDisabled = useRbacNavDisabled('CMS')
  const alertBannerOrderingLabel = usePermissionLabel('OrderingManagement')
  const alertBannerCmsLabel = usePermissionLabel('CMS')
  const alertBannerDisabled = orderingMgmtDisabled && cmsDisabled
  const alertBanner = {
    disabled: alertBannerDisabled,
    disabledReason: formatPermissionReasonOr(
      [alertBannerOrderingLabel, alertBannerCmsLabel],
      alertBannerDisabled
    ),
  }

  const recoveryAiEnabled = useFlag('recovery-ai', false)
  // Entitlement probe (ungated server-side, see api/recoveryAi). It decides WHICH
  // surface /recovery_ai is for this merchant, and therefore whether the link may
  // be gated at all: not set up ⇒ the page is pure marketing copy, reachable by
  // any authenticated merchant user; set up ⇒ it is the live console and keeps
  // today's MerchantSettings gate. Skipped while the LD flag is off — the link
  // isn't rendered then, so there is nothing to gate.
  const { data: recoveryStatus } = useGetRecoveryStatusQuery(undefined, {
    skip: !recoveryAiEnabled,
  })
  // Called unconditionally (hooks rules) and only APPLIED for a set-up merchant.
  const recoveryAiGate = useRbacNavDisabledWithReason('MerchantSettings')
  // In-flight (or unavailable — a 404 on FE/BE deploy skew leaves `data`
  // undefined forever) resolves to ENABLED. The un-set-up case is the common one
  // and its page is open to everyone, so erring this way costs a set-up
  // merchant's permission-less viewer at most one early click that the route gate
  // then redirects; erring the other way would flash a disabled, tooltip'd link
  // on every page load for every merchant, including the ones who can use it.
  const recoveryAi = recoveryStatus?.configured
    ? recoveryAiGate
    : { disabled: false, disabledReason: undefined }

  // Order ratings (the Looker report) and Order feedback (settings/inbox) used
  // to be tabs on one page behind a combined OR gate — Feedback (Ratings &
  // Reviews) unlocked Details, LocationReports/NpsReports unlocked Overview.
  // AI-1257 split them into separate pages, so each nav link below now gates
  // on only its own page's permission instead of the OR.
  const ability = useAbility()
  const rbacEnabled = useFlag('role-based-access-control', false)
  const canViewOrderFeedbackDetails = ability.can('access', 'Feedback')
  const canViewOrderFeedbackOverview = rbacEnabled
    ? ability.can('access', 'LocationReports')
    : ability.can('access', 'NpsReports')
  const orderFeedbackFeedbackLabel = usePermissionLabel('Feedback')
  const orderFeedbackOverviewLabel = usePermissionLabel('LocationReports')
  const orderFeedbackDisabled = !canViewOrderFeedbackDetails
  const orderFeedbackReason = formatPermissionReason(
    orderFeedbackFeedbackLabel,
    orderFeedbackDisabled
  )
  const orderRatingsDisabled = !canViewOrderFeedbackOverview
  const orderRatingsReason = formatPermissionReason(
    orderFeedbackOverviewLabel,
    orderRatingsDisabled
  )
  // The LEGACY "NPS feedback" entry below opens scenes/Reports/NpsReport, a
  // different page that is still gated on useReportPermission alone. It must
  // NOT inherit the OR above: Ratings & Reviews unlocks nothing on that report,
  // so a Feedback-only viewer following an enabled link would land on its
  // full-page lock panel.
  const npsFeedbackDisabled = !canViewOrderFeedbackOverview
  const npsFeedbackReason = usePermissionReason(
    'LocationReports',
    npsFeedbackDisabled
  )

  const merchant = useCurrentMerchant()
  const revenueCentersFlag = useFlag('toast-revenue-center-enabled-mx', false)
  const revenueCentersEnabled =
    revenueCentersFlag && Boolean(merchant?.supports_revenue_centers)

  const feedbackEnabled = useMerchantFeedbackEnabled()
  // A merchant with feedback disabled has nothing to respond to and nothing
  // to transition away from, regardless of the flag's state.
  const showLegacySection = feedbackEnabled

  return (
    <>
      {recoveryAiEnabled && (
        <>
          {!isLeftNav && (
            <SectionHeader text={t('thanx_ai')} iconName="thanxAi" />
          )}
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('recovery_ai')}
            link="/recovery_ai"
            disabled={recoveryAi.disabled}
            disabledReason={recoveryAi.disabledReason}
          />
          {/* 16px group gap between sections (Figma Global Nav node 1:1141),
              not the 24px `mb-m` used elsewhere — that reads oversized once
              multiple section gaps stack in one dropdown. */}
          {!isLeftNav && <div className="mb-s" />}
        </>
      )}
      {/* Digital Ordering groups Locations with the ordering/reporting-adjacent
          links below under one label, following the same category pattern as
          the Customers and Settings menus: a header row, dropdown only. No
          rule, no wrapping div, no indentation/border, and the links keep
          their normal (non-legacy) color and font. The left nav lists links
          flat like those menus do. */}
      {!isLeftNav && (
        <SectionHeader text={t('digital_ordering')} iconName="bag" />
      )}
      {/* `/locations` is the operator-facing replacement for the
         legacy `/ordering_management` page. The legacy nav link was
         intentionally removed from this sub-nav 2026-05-12 (commit
         056ff6f95) because the two surfaces show the same data — the
         new Locations page is canonical and the legacy route stays
         direct-URL-accessible only during the rollout-coordination
         window. A re-enable of the legacy link is not a planned
         rollback path; if the new Locations page misbehaves, the
         `operations-center` LD flag flips off and the whole sub-nav
         disappears (and the legacy `/ordering_management` link is
         already restored in the Brand-&-Content nav under flag-off).
         No new flag here. */}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('locations')}
        link="/locations"
        disabled={locations.disabled}
        disabledReason={locations.disabledReason}
      />
      {/* POS Configurations (Menu Errors) is Toast-specific today — only
          shown for providers whose menu-errors feed exists (DATA-5395).
          Extend MENU_ERRORS_PROVIDERS in useOrderingAccess to add a provider.
          The page route guards on the same capability for direct-URL access. */}
      {supportsMenuErrors && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('pos_configurations')}
          link="/pos_configurations"
          disabled={locations.disabled}
          disabledReason={locations.disabledReason}
        />
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('delivery_location_settings')}
        link="/delivery_location_settings"
        disabled={deliveryLocationSettings.disabled}
        disabledReason={deliveryLocationSettings.disabledReason}
      />
      {revenueCentersEnabled && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('revenue_centers')}
          link="/settings/revenue_centers"
          disabled={!orderingEnabled}
          disabledReason={orderingReason}
        />
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('ordering_error_report')}
        link="/reports/ordering_error_report"
        disabled={!orderingEnabled}
        disabledReason={orderingReason}
      />
      {/* Order feedback (settings/inbox) and Order ratings (the 👍/👎 Looker
          report) — split from one combined page/nav-entry into two (AI-1257 +
          AI-1337). Both are universally reachable (Order Status is GA;
          leadership wants both reachable by every merchant), but still
          require ordering capability like their OC siblings above: a
          merchant with no ordering provider can never generate a rating or
          feedback. CASL permission (above) drives the disabled/reason state. */}
      {supportsOrderingProvider && (
        <>
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('order_ratings')}
            link="/order_ratings"
            disabled={orderRatingsDisabled}
            disabledReason={orderRatingsReason}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('order_feedback')}
            link="/order_feedback"
            disabled={orderFeedbackDisabled}
            disabledReason={orderFeedbackReason}
            status="new"
          />
        </>
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('alert_banner')}
        link="/alert_banners"
        disabled={alertBanner.disabled}
        disabledReason={alertBanner.disabledReason}
      />
      {/* Support gets its own header/icon, matching Digital ordering above —
          previously a flat, ungrouped link. Same 16px group gap as ThanxAI's
          above. */}
      {!isLeftNav && <div className="mb-s" />}
      {!isLeftNav && (
        <SectionHeader text={t('support')} iconName="userSupport" />
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('end_user_support')}
        link="/user_support"
        disabled={userSupport.disabled}
        disabledReason={userSupport.disabledReason}
      />
      {/* Legacy section: gated on the merchant's feedback_enabled — a
          merchant who already had NPS off has nothing to respond to or
          transition away from. The NPS Feedback entry opens the
          NPS→RecoveryAI transition modal via onNpsClick. */}
      {showLegacySection && (
        <div
          className={`${isLeftNav ? 'mt-xs mx-m ' : ''}${css({
            border: `1px solid ${palette.grey10}`,
          })}`}
        >
          <SectionHeader
            isLeftNav={isLeftNav}
            text={navT('legacy.reports')}
            iconName="scroll"
            isLegacy
            learnMore={!isLeftNav}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={() => {
              onClick()
              onNpsClick?.()
            }}
            text={t('nps_feedback')}
            disabled={npsFeedbackDisabled}
            disabledReason={npsFeedbackReason}
            isLegacy
          />
        </div>
      )}
    </>
  )
}

export default OperationsCenterContent
